import {
  AppBar,
  Box,
  Button,
  ClickAwayListener,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { FC, PropsWithChildren, useContext, MouseEvent } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { HeaderContext, NavChildOption, NavOption } from "./context";
import { Container } from "../Container";
import LogoIcon from "../../assets/icons/Logo.svg";
import styles from "./styles.module.scss";
import clsx from "clsx";

const HeaderSubNav: FC<PropsWithChildren<{ items: NavChildOption[] }>> = ({
  items,
}) => {
  const { setOpenedSubMenu, setMenuIsOpen } = useContext(HeaderContext);
  const handleClick = (e: MouseEvent, item: NavChildOption): void => {
    if (!item.link) {
      e.preventDefault();
    }
    setOpenedSubMenu(null);
    setMenuIsOpen(false);
  };

  return (
    <Box className={styles.subNav}>
      <Container className={styles.subNavContainer}>
        <List className={styles.subNavList}>
          {items.map((item) => {
            const IconComponent = item.icon;
            return (
              <ListItem key={item.id} className={styles.subNavListItem}>
                <a
                  href={item.link || "#"}
                  onClick={(e): void => {
                    handleClick(e, item);
                  }}
                  className={styles.subNavItem}
                >
                  <Box className={styles.subNavItemIcon}>
                    <img src={IconComponent} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      className={styles.subNavItemTitle}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.subNavItemDesc}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </a>
              </ListItem>
            );
          })}
        </List>
      </Container>
    </Box>
  );
};

const HeaderNavItem: FC<{ option: NavOption }> = ({ option }) => {
  const { setOpenedSubMenu, openedSubMenu, setMenuIsOpen } =
    useContext(HeaderContext);
  const handleClick = (e: MouseEvent, option: NavOption): void => {
    if (!option.link) {
      e.preventDefault();
      setOpenedSubMenu(
        openedSubMenu && openedSubMenu === option.id ? null : option.id,
      );
    } else {
      setOpenedSubMenu(null);
      setMenuIsOpen(false);
    }
  };
  const handleClickAway = (): void => {
    if (option.id === openedSubMenu) {
      setOpenedSubMenu(null);
    }
  };
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <ListItem
        className={clsx(styles.navListItem, {
          [styles.opened]: openedSubMenu === option.id,
        })}
      >
        <a
          href={option.link || "#"}
          onClick={(e): void => {
            handleClick(e, option);
          }}
          className={styles.navListItemLink}
        >
          {option.title}
          {!option.link && (
            <Box component="span" className={styles.navListItemLinkIcon}>
              <KeyboardArrowDownIcon />
            </Box>
          )}
        </a>
        {!option.link && option?.children && (
          <HeaderSubNav items={option.children} />
        )}
      </ListItem>
    </ClickAwayListener>
  );
};

const HeaderNav: FC = () => {
  const { config: navConfig } = useContext(HeaderContext);

  return (
    <Box component="nav" className={styles.nav}>
      <List className={styles.navList}>
        {navConfig.map((option) => {
          return <HeaderNavItem key={option.id} option={option} />;
        })}
      </List>
    </Box>
  );
};

export const Header: FC = () => {
  const { menuIsOpen, setMenuIsOpen } = useContext(HeaderContext);

  const SIGN_IN_LINK = "https://dashboard.powerlead.com/sign-in";
  const SIGN_UP_LINK = "https://dashboard.powerlead.com/signup";


  return (
    <AppBar className={styles.appBar} component="header" position="sticky">
      <Container className={styles.container}>
        <a href="/" className={styles.logo}>
          <img src={LogoIcon} />
        </a>
        <Box
          className={clsx(styles.menu, {
            [styles.menuIsOpen]: menuIsOpen,
          })}
        >
          <HeaderNav />
          <Box className={styles.appBarButtons}>
            <a href="/get-my-demo" className={styles.appBarReqButton}>
              Request a demo
            </a>
            <Button
              className={styles.appBarButton}
              style={{ color: "black" }}
	      href={SIGN_IN_LINK}
            >
              Log in
            </Button>
            <Button
              color="secondary"
              variant="contained"
              className={styles.appBarButton}
              style={{ backgroundImage: "var(--pw-gradient)" }}
	      href={SIGN_UP_LINK}
            >
              Start for free
            </Button>
          </Box>
        </Box>
        <Box
          className={clsx("isMobile", styles.menuToggle, {
            [styles.menuIsOpen]: menuIsOpen,
          })}
          onClick={(): void => {
            setMenuIsOpen(!menuIsOpen);
          }}
        >
          <Box component="span"></Box>
          <Box component="span"></Box>
          <Box component="span"></Box>
        </Box>
      </Container>
    </AppBar>
  );
};
